import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import {
  ChatMessage,
  LastDecisionMessage,
  MinimalChatMessage,
  ChatMessageIds,
} from "../types/chat";
import { GoBackStackItem } from "../types/hooks";
import { CHAT_CONSTANTS } from "../utils/constants";
import { makeFlagMessageRequest } from "../services/ChatService";
import { wrapTyping } from "../utils/typingGate";
import { getWelcomeMessage } from "../utils/messages";

interface FeedbackModalState {
  isOpen: boolean;
  currentQueryId: string | null;
}

interface MessagesContextType {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  addMessage: (
    text: string,
    messageType: "bot" | "user",
    messageId?: string,
    media?: string,
    audio?: string[],
    nodeUuid?: string,
    options?: Array<[string, string]>,
    origin?: string
  ) => ChatMessage;
  addMessageObject: (message: ChatMessage) => void;
  clearMessages: () => void;
  removeMessage: (messageId: string) => void;
  removeDefinitionMessages: () => void;
  updateMessageFlag: (messageId: string, isFlagged: boolean) => void;
  lastDecisionMessage: LastDecisionMessage | null;
  setLastDecisionMessage: React.Dispatch<
    React.SetStateAction<LastDecisionMessage | null>
  >;
  recordDecisionMade: (
    botMessageId: string,
    selectedOption: [string, string],
    selectedIndex: number,
    allOptions: [string, string][],
    nodeUuid: string
  ) => void;
  clearLastDecision: () => void;
  setNewDecisionMessage: (parentNodeUuid: string, nodeUuid: string) => void;
  addStillHavingProblemOption: (
    options: Array<[string, string]>,
    lastMessageText: string
  ) => Array<[string, string]>;
  getLastVideoMessageId: () => string | null;

  // GoBackStack functionality
  goBackStack: GoBackStackItem[];
  addToStack: (nodeUuid: string, messageId: string) => void;
  canGoBack: boolean;
  clearStack: () => void;
  setGoBackStack: React.Dispatch<React.SetStateAction<GoBackStackItem[]>>;

  // Feedback modal state
  feedbackModal: FeedbackModalState;
  setFeedbackModal: (modal: FeedbackModalState) => void;
  openFeedbackModal: (queryId: string) => void;
  closeFeedbackModal: () => void;

  // Session state
  sessionCreateTime: string;
  setSessionCreateTime: (time: string) => void;

  // Message actions
  onFeedbackSubmit: (messageId: string, feedback: string) => Promise<void>;

  // Conversation state management
  getPrunedForSend: (options?: {
    sizeLimit?: number;
    maxAgeMs?: number;
    nowMs?: number;
    maxSingleTextLen?: number;
  }) => string[];
  getMessageIds: () => string[];

  // Refs for current values (for use in callbacks)
  messagesRef: React.MutableRefObject<ChatMessage[]>;
  lastDecisionRef: React.MutableRefObject<LastDecisionMessage | null>;
  goBackStackRef: React.MutableRefObject<GoBackStackItem[]>;
}

const MessagesContext = createContext<MessagesContextType | undefined>(
  undefined
);

interface MessagesProviderProps {
  children: ReactNode;
}

export const MessagesProvider: React.FC<MessagesProviderProps> = ({
  children,
}) => {
  // Initialize with welcome message - using function initializer to ensure UUID is generated only once
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    return [getWelcomeMessage()];
  });
  const [lastDecisionMessage, setLastDecisionMessage] =
    useState<LastDecisionMessage | null>(null);
  const [goBackStack, setGoBackStack] = useState<GoBackStackItem[]>([]);
  const [feedbackModal, setFeedbackModal] = useState<FeedbackModalState>({
    isOpen: false,
    currentQueryId: null,
  });
  const [sessionCreateTime, setSessionCreateTime] = useState<string>("");

  // Refs for current values
  const messagesRef = useRef(messages);
  const lastDecisionRef = useRef(lastDecisionMessage);
  const goBackStackRef = useRef(goBackStack);

  // Update refs when state changes
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    lastDecisionRef.current = lastDecisionMessage;
  }, [lastDecisionMessage]);

  useEffect(() => {
    goBackStackRef.current = goBackStack;
  }, [goBackStack]);

  const addMessage = useCallback(
    (
      text: string,
      messageType: "bot" | "user",
      messageId?: string,
      media?: string,
      audio?: string[],
      nodeUuid?: string,
      options?: Array<[string, string]>,
      origin?: string
    ) => {
      const newMessage: ChatMessage = {
        id: crypto.randomUUID(),
        messageId: messageId || crypto.randomUUID(),
        text,
        messageType,
        dateTime: new Date(),
        media,
        audio,
        nodeUuid: nodeUuid || "00000000-0000-0000-0000-000000000000",
        options,
        origin,
        isFlagged: false,
      };
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    []
  );

  const addMessageObject = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLastDecisionMessage(null);
    setGoBackStack([]);
  }, []);

  // GoBackStack functions
  const addToStack = useCallback((nodeUuid: string, messageId: string) => {
    if (nodeUuid && nodeUuid !== CHAT_CONSTANTS.NULL_UUID) {
      setGoBackStack((prev) => {
        const newStack = [...prev];
        if (
          newStack.length > 0 &&
          newStack[newStack.length - 1].nodeUuid ===
            CHAT_CONSTANTS.PLACEHOLDER_UUID
        ) {
          newStack.pop();
        }
        newStack.push({ nodeUuid, messageId });
        return newStack;
      });
    } else if (nodeUuid === CHAT_CONSTANTS.NULL_UUID) {
      setGoBackStack((prev) => [
        ...prev,
        { nodeUuid: CHAT_CONSTANTS.PLACEHOLDER_UUID, messageId: "" },
      ]);
    }
  }, []);

  const canGoBack = useMemo(() => goBackStack.length >= 2, [goBackStack]);

  const clearStack = useCallback(() => {
    setGoBackStack([]);
  }, []);

  const removeMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  const removeDefinitionMessages = useCallback(() => {
    setMessages((prev) =>
      prev.filter((msg) => msg.endpoint_result !== "definition")
    );
  }, []);

  const updateMessageFlag = useCallback(
    (messageId: string, isFlagged: boolean) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.messageId === messageId ? { ...message, isFlagged } : message
        )
      );
    },
    []
  );

  const recordDecisionMade = useCallback(
    (
      botMessageId: string,
      selectedOption: [string, string],
      selectedIndex: number,
      allOptions: [string, string][],
      nodeUuid: string
    ) => {
      if (lastDecisionMessage && lastDecisionMessage.nodeUuid === nodeUuid) {
        setLastDecisionMessage({
          messageId: botMessageId,
          selectedOptions: [
            ...lastDecisionMessage.selectedOptions,
            selectedOption,
          ],
          selectedIndexes: [
            ...lastDecisionMessage.selectedIndexes,
            selectedIndex,
          ],
          allOptions,
          nodeUuid: nodeUuid,
        });
      } else {
        setLastDecisionMessage({
          messageId: botMessageId,
          selectedOptions: [selectedOption],
          selectedIndexes: [selectedIndex],
          allOptions,
          nodeUuid: nodeUuid,
        });
      }
    },
    [lastDecisionMessage]
  );

  const clearLastDecision = useCallback(() => {
    setLastDecisionMessage(null);
  }, []);

  const setNewDecisionMessage = useCallback(
    (parentNodeUuid: string, nodeUuid: string) => {
      const lastBotMessage = messages.find(
        (msg) => msg.messageType === "bot" && msg.nodeUuid === parentNodeUuid
      ) as ChatMessage;
      if (
        lastBotMessage &&
        lastBotMessage.nodeUuid !== lastDecisionMessage?.nodeUuid &&
        lastBotMessage.options &&
        lastBotMessage.options.length > 1
      ) {
        const selectedIndex = lastBotMessage.options.findIndex(
          ([, uuid]) => uuid === nodeUuid
        );
        if (selectedIndex !== -1) {
          const optionText = lastBotMessage.options[selectedIndex][0];

          recordDecisionMade(
            lastBotMessage.messageId || lastBotMessage.id,
            [optionText, nodeUuid],
            selectedIndex,
            lastBotMessage.options,
            parentNodeUuid
          );
        }
      } else {
        for (const [
          index,
          option,
        ] of lastDecisionMessage?.allOptions.entries() || []) {
          if (option[1] == nodeUuid) {
            recordDecisionMade(
              lastBotMessage.messageId || lastBotMessage.id,
              [option[0], option[1]],
              index,
              lastDecisionMessage?.allOptions || [],
              lastDecisionMessage?.nodeUuid || ""
            );
            break;
          }
        }
      }
    },
    [messages, lastDecisionMessage, recordDecisionMade]
  );

  const addStillHavingProblemOption = useCallback(
    (options: Array<[string, string]>, lastMessageText: string) => {
      // Check if we're at a leaf node (no options) and have a last decision message
      const isAtLeafNode =
        !options ||
        options.length === 0 ||
        options.every((opt) => opt[0].includes("[Next]")); // Still considered a leaf node if every option is a suggest next step
      if (isAtLeafNode && lastDecisionMessage) {
        // Add an option with uuid of decision node or non-traversed option
        const stillHavingProblemText =
          "I am still experiencing this problem" +
          "[c]I tried that[/c]" +
          "[c]The problem is happening again[/c]" +
          "[c]That did not fix the problem[/c]" +
          "[c]Still not working[/c]" +
          "[c]Nothing is happening[/c]" +
          "[c]I tried '" +
          lastMessageText +
          "and it did not work[/c]";
        // the options that have not been traversed yet
        const optionsLeft =
          lastDecisionMessage.allOptions.length -
          lastDecisionMessage.selectedIndexes.length;
        if (optionsLeft === 1) {
          // get the uuid of the final option and add it to the options
          const finalOptionUuid = lastDecisionMessage.allOptions.filter(
            (option) =>
              !lastDecisionMessage.selectedIndexes.includes(
                lastDecisionMessage.allOptions.indexOf(option)
              )
          )[0][1];
          options.push([stillHavingProblemText, finalOptionUuid]);
        } else if (optionsLeft > 0) {
          // get the uuid of the last decision node and present that if matched
          options.push([stillHavingProblemText, lastDecisionMessage.nodeUuid]);
        } else {
          // all the options in the last decision message have been selected, if it matches the frontend will fall back to customer support tree
          options.push([stillHavingProblemText, lastDecisionMessage.nodeUuid]);
        }
      }

      return options;
    },
    [lastDecisionMessage]
  );

  const getLastVideoMessageId = useCallback(() => {
    const videoMessages = messagesRef.current.filter(
      (msg) => msg.messageType === "bot" && msg.media?.includes(".mp4")
    );
    return videoMessages[videoMessages.length - 1]?.messageId || null;
  }, []);

  //CODE FOR MANAGING CONVO STATE TO PASS TO BACKEND
  const toMillis = (dt: Date | string): number => {
    if (dt instanceof Date) return dt.getTime();
    const n = Date.parse(dt);
    return Number.isFinite(n) ? n : 0;
  };

  // helper: keep only the fields you care about
  const projectMinimal = useCallback((m: ChatMessage): MinimalChatMessage => {
    const out: MinimalChatMessage = {
      id: m.id,
      text: m.text,
      messageType: m.messageType,
      endpoint_result: m.endpoint_result,
    };
    if (m.nodeUuid) out.nodeUuid = m.nodeUuid;
    if (m.options && m.options.length) out.options = m.options;
    if (m.endpoint_result) out.endpoint_result = m.endpoint_result;
    if (m.comingFrom) out.comingFrom = m.comingFrom;
    if (m.pruned) out.pruned = m.pruned;
    return out;
  }, []);

  const convoIds = useCallback((m: ChatMessage): ChatMessageIds => {
    const out: ChatMessageIds = {
      id: m.id,
    };
    if (m.nodeUuid) out.nodeUuid = m.nodeUuid;
    if (m.comingFrom) out.comingFrom = m.comingFrom;
    if (m.pruned) out.pruned = m.pruned;
    if (m.endpoint_result) out.endpoint_result = m.endpoint_result;

    return out;
  }, []);

  const jsonSize = (arr: MinimalChatMessage[]) => JSON.stringify(arr).length;

  /**
   * Returns a NEW array (does not mutate state) containing only:
   *  - messages ≤ maxAgeMs old (default 2h)
   *  - projected to { id, text, nodeUuid?, options?, messageType }
   *  - pruned from the oldest until JSON.stringify(payload).length ≤ sizeLimit (default 10_000)
   *
   * You can pass serverNowMs if you want FE/BE to agree on "now".
   */
  const getPrunedForSend = useCallback(
    ({
      sizeLimit = 10_000,
      maxAgeMs = 2 * 60 * 60 * 1000, // 2 hours
      nowMs = Date.now(),
      // optional: hard safety for single huge message
      maxSingleTextLen = 9_000,
    }: {
      sizeLimit?: number;
      maxAgeMs?: number;
      nowMs?: number; // pass server time here if you want
      maxSingleTextLen?: number; // trims a single massive text if needed
    } = {}): string[] => {
      const cutoff = nowMs - maxAgeMs;

      // 1) age filter
      const recent = messages.filter((m) => toMillis(m.dateTime) >= cutoff);

      // 2) sort oldest→newest (so we drop from the front if needed)
      recent.sort((a, b) => toMillis(a.dateTime) - toMillis(b.dateTime));

      // 3) project to minimal payload
      let out = recent.map(projectMinimal);

      // 4) prune oldest until under size cap
      while (out.length > 0 && jsonSize(out) > sizeLimit) {
        out = out.slice(1);
      }

      // 5) optional guard: if one message alone is too big, truncate its text
      if (out.length === 1 && jsonSize(out) > sizeLimit) {
        const only = { ...out[0] };
        if (
          typeof only.text === "string" &&
          only.text.length > maxSingleTextLen
        ) {
          // trim enough to fit; keep a small suffix marker
          const overBy = jsonSize([only]) - sizeLimit;
          const safety = Math.max(overBy + 20, 0);
          const targetLen = Math.max(0, maxSingleTextLen - safety);
          only.text = only.text.slice(0, targetLen) + "…";
          out = [only];
        } else {
          // if still too large and can't trim, drop it
          out = [];
        }
      }

      return out.map((m) => JSON.stringify(m));
    },
    [messages, projectMinimal]
  );

  const getMessageIds = useCallback((): string[] => {
    const bots = messages.filter((m) => m.messageType == "bot");
    let out = bots.map(convoIds);
    return out.map((m) => JSON.stringify(m));
  }, [messages, convoIds]);

  // Feedback modal helper functions
  const openFeedbackModal = useCallback((queryId: string) => {
    setFeedbackModal({ isOpen: true, currentQueryId: queryId });
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackModal({ isOpen: false, currentQueryId: null });
  }, []);

  // Feedback submission function
  const onFeedbackSubmit = useCallback(
    async (messageId: string, feedback: string) => {
      await wrapTyping(async () => {
        try {
          const response = await makeFlagMessageRequest({
            query_id: messageId,
            user_feedback: feedback,
            session_create_time: sessionCreateTime,
          });

          updateMessageFlag(messageId, true);

          addMessage(
            response.text,
            "bot",
            response.query_id,
            "",
            [],
            "00000000-0000-0000-0000-000000000000",
            [],
            "Message Feedback"
          );
        } catch (error) {
          console.error("Flag request error:", error);
          addMessage(
            CHAT_CONSTANTS.MESSAGES.FEEDBACK_ERROR,
            "bot",
            messageId,
            "",
            [],
            "00000000-0000-0000-0000-000000000000",
            [],
            "Message Feedback Error"
          );
        }
      });
    },
    [sessionCreateTime, updateMessageFlag, addMessage]
  );

  const value = useMemo(
    () => ({
      messages,
      setMessages,
      addMessage,
      addMessageObject,
      clearMessages,
      removeMessage,
      removeDefinitionMessages,
      updateMessageFlag,
      lastDecisionMessage,
      setLastDecisionMessage,
      recordDecisionMade,
      clearLastDecision,
      setNewDecisionMessage,
      addStillHavingProblemOption,
      getLastVideoMessageId,
      // GoBackStack functionality
      goBackStack,
      addToStack,
      canGoBack,
      clearStack,
      setGoBackStack,
      // Feedback modal state
      feedbackModal,
      setFeedbackModal,
      openFeedbackModal,
      closeFeedbackModal,
      // Session state
      sessionCreateTime,
      setSessionCreateTime,
      // Message actions
      onFeedbackSubmit,
      // Conversation state management
      getPrunedForSend,
      getMessageIds,
      // Refs for current values
      messagesRef,
      lastDecisionRef,
      goBackStackRef,
    }),
    [
      messages,
      addMessage,
      addMessageObject,
      clearMessages,
      removeMessage,
      removeDefinitionMessages,
      updateMessageFlag,
      lastDecisionMessage,
      recordDecisionMade,
      clearLastDecision,
      setNewDecisionMessage,
      addStillHavingProblemOption,
      getLastVideoMessageId,
      goBackStack,
      addToStack,
      canGoBack,
      clearStack,
      setGoBackStack,
      feedbackModal,
      setFeedbackModal,
      openFeedbackModal,
      closeFeedbackModal,
      sessionCreateTime,
      setSessionCreateTime,
      onFeedbackSubmit,
      getPrunedForSend,
      getMessageIds,
      messagesRef,
      lastDecisionRef,
      goBackStackRef,
    ]
  );

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = (): MessagesContextType => {
  const context = useContext(MessagesContext);
  if (context === undefined) {
    throw new Error("useMessages must be used within a MessagesProvider");
  }
  return context;
};
